/**
 * Persistent per-player planet visual configuration.
 * Generated once per planet and stored in backend state.
 * Ensures each player sees a consistent, deterministic planet.
 */

const hash = (v: string) => v.split('').reduce((a, c) => (a * 33 + c.charCodeAt(0)) >>> 0, 5381);
const rng = (seed: number) => () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
export const PLANET_CONFIG_LAYOUT_VERSION = 2;

export interface PlanetPalette {
  baseTint: number;
  waterColor: number;
  atmosphereColor: number;
  continentTint: number;
  biomeTint: number;
}

export interface ContinentConfig {
  assetIndex: number;
  slotIndex: number;
  angle: number;
  radial: number;
  rotation: number;
  scale: number;
}

export interface PlanetConfig {
  seed: string;
  baseIndex: number;
  palette: PlanetPalette;
  asteroidCount: number;
  layoutVersion: number;
  asteroidBelt?: 'none' | 'sparse' | 'broken' | 'dense' | 'cluster'; // legacy fallback
  continents: ContinentConfig[];
}

const CONTINENT_SLOTS = [
  { id: 'slot_01', angle: 24, radial: 0.5 },
  { id: 'slot_02', angle: 114, radial: 0.54 },
  { id: 'slot_03', angle: 204, radial: 0.48 },
  { id: 'slot_04', angle: 294, radial: 0.56 },
];

export const REALISTIC_WATER_BLUE_OPTIONS = [
  0x0b3d5c,
  0x0d4f73,
  0x145f85,
  0x1a6f9a,
  0x247fa8,
  0x2a8fbd,
  0x256f9f,
  0x1b5d89,
  0x123f66,
] as const;

const PALETTE_PRESETS: PlanetPalette[] = [
  { baseTint: 0x7a6a52, waterColor: 0x145f85, atmosphereColor: 0xf0c58c, continentTint: 0x93b66f, biomeTint: 0x2e7d32 },
  { baseTint: 0x645748, waterColor: 0x1a6f9a, atmosphereColor: 0xc8d8ff, continentTint: 0x7a8c5a, biomeTint: 0x3a8c40 },
  { baseTint: 0x8a7762, waterColor: 0x0d4f73, atmosphereColor: 0xe4d28f, continentTint: 0xa3c47a, biomeTint: 0x228b22 },
  { baseTint: 0x6b5e4f, waterColor: 0x247fa8, atmosphereColor: 0xb6c7f2, continentTint: 0x8faa5c, biomeTint: 0x4a9b4a },
  { baseTint: 0x5c5045, waterColor: 0x123f66, atmosphereColor: 0xd7ac83, continentTint: 0x7d9950, biomeTint: 0x3d8b3d },
  { baseTint: 0x7d6e58, waterColor: 0x2a8fbd, atmosphereColor: 0xf2d7a1, continentTint: 0x99b870, biomeTint: 0x5aaa5a },
];

function pickAsteroidCount(v: number): number {
  // 30% chance of no asteroids, 70% chance of 7-20 asteroids
  if (v < 0.3) return 0;
  return 7 + Math.floor((v - 0.3) / 0.7 * 14);
}

/**
 * Generate a deterministic planet config from a seed string (e.g. user_id).
 * Same seed always produces the same planet appearance.
 */
export function generatePlanetConfig(seedSource: string, stage: number): PlanetConfig {
  const seed = hash(seedSource);
  const seeded = rng(seed);

  const palettePreset = PALETTE_PRESETS[Math.floor(seeded() * PALETTE_PRESETS.length)];
  const waterSeeded = rng(seed ^ 0x5f3759df);
  const palette = {
    ...palettePreset,
    waterColor: REALISTIC_WATER_BLUE_OPTIONS[Math.floor(waterSeeded() * REALISTIC_WATER_BLUE_OPTIONS.length)],
  };
  const baseIndex = Math.floor(seeded() * 6); // 6 planet bases
  const asteroidCount = pickAsteroidCount(seeded());

  // Continents appear from stage 1+ with wider angular spacing to avoid overlap.
  const continentCount = stage >= 1 ? 3 + Math.floor(seeded() * 2) : 0;
  const shuffledSlots = CONTINENT_SLOTS.map((s, i) => ({ ...s, idx: i }))
    .sort(() => seeded() - 0.5)
    .slice(0, continentCount);

  const continents: ContinentConfig[] = shuffledSlots.map((slot) => ({
    assetIndex: Math.floor(seeded() * 10), // 10 continent variants
    slotIndex: slot.idx,
    angle: slot.angle + (seeded() - 0.5) * 5,
    radial: slot.radial + (seeded() - 0.5) * 0.025,
    rotation: (seeded() - 0.5) * 0.6,
    scale: 0.66 + seeded() * 0.12,
  }));

  return {
    seed: seedSource,
    baseIndex,
    palette,
    asteroidCount,
    layoutVersion: PLANET_CONFIG_LAYOUT_VERSION,
    continents,
  };
}

/**
 * Load or generate planet config from backend state.
 * If visual_config exists in planet state, use it.
 * Otherwise generate from user_id.
 */
export function getPlanetConfig(
  userId: string | null,
  stage: number,
  visualConfig?: Record<string, unknown> | null,
): PlanetConfig {
  if (
    visualConfig &&
    typeof visualConfig === 'object' &&
    Number((visualConfig as { layoutVersion?: unknown }).layoutVersion || 0) >= PLANET_CONFIG_LAYOUT_VERSION
  ) {
    return visualConfig as unknown as PlanetConfig;
  }
  return generatePlanetConfig(userId || 'default', stage);
}
